/**
 * Every message sent about one claim (WPS-175 requirement 3), plus the
 * claim's forms: the shared record card, with "Form" in Create New and the
 * forms sent from the claim listed under its messages.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ClipboardList } from "lucide-react";

import { RecordCommunicationsCard } from "@/admin/components/composer/record-communications-card";
import { RecordForms } from "@/admin/components/forms/record-forms";
import { SendFormDialog } from "@/admin/components/forms/send-form-dialog";
import { useFormTemplates } from "@/admin/hooks/use-forms";
import { usePermissions } from "@/admin/hooks/use-permissions";
import { SEND_REQUESTS } from "@/admin/lib/permissions";

export function ClaimCommunicationsCard({
  claimUuid,
  folded,
  onToggleFold,
}: {
  claimUuid: string;
  folded?: boolean;
  onToggleFold?: () => void;
}) {
  const { t } = useTranslation();
  const [sendingForm, setSendingForm] = useState(false);
  const { can } = usePermissions();
  // Offered only once the org has published a form that can go out from a
  // claim; the send dialog asks the same list, so it opens already loaded.
  const { data: formTemplates = [] } = useFormTemplates({
    recordType: "claim",
    activeOnly: true,
    publishedOnly: true,
  });
  const canSendForms = can(SEND_REQUESTS) && formTemplates.length > 0;

  return (
    <RecordCommunicationsCard
      recordType="claim"
      recordUuid={claimUuid}
      testIdPrefix="claim-comms"
      emptyLabel={t("claimComms.empty", "Nothing has been sent on this claim yet.")}
      canAttachFiles
      folded={folded}
      onToggleFold={onToggleFold}
      extraCreateOptions={
        canSendForms
          ? [
              {
                key: "form",
                icon: ClipboardList,
                label: t("claimComms.create.form", "Form"),
                hint: t(
                  "claimComms.create.formHint",
                  "Ask anyone to fill in one of your forms",
                ),
                testId: "claim-comms-create-form",
                onSelect: () => setSendingForm(true),
              },
            ]
          : undefined
      }
    >
      <RecordForms recordType="claim" recordUuid={claimUuid} />
      {sendingForm ? (
        <SendFormDialog
          recordType="claim"
          recordUuid={claimUuid}
          onClose={() => setSendingForm(false)}
        />
      ) : null}
    </RecordCommunicationsCard>
  );
}
